import RecentWebsiteLaunches from "@/components/RecentWebsiteLaunches";
import { MockupLightbox } from "@/components/MockupLightbox";
import { PortfolioGallery } from "@/components/PortfolioGallery";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AuthorityBadges } from "@/components/AuthorityBadges";
import { useEffect, useRef, useState, type ComponentProps } from "react";
import {
  Rocket, Check, Zap, Palette, ShieldCheck, ArrowRight,
  Wrench, Stethoscope,
  Briefcase, Dumbbell, Building2, Target,
  Accessibility, Mail, Bot,
  Loader2, CheckCircle2, Menu, X, Layers, PenTool,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LeadFormDialog, openLeadFormDialog } from "@/components/LeadFormDialog";
import { HeroLeadForm } from "@/components/HeroLeadForm";


import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { SpinToWinPopup } from "@/components/SpinToWinPopup";
import FloatingVideoWidget from "@/components/FloatingVideoWidget";
import heroPeople from "@/assets/chicago-hero.jpg";
import heroIntroVideo from "@/assets/chicago-hero-intro-video-hd.mp4.asset.json";
import sierraStairs from "@/assets/sierra-stairs.jpg.asset.json";
import portfolioSpeakingAuthor from "@/assets/portfolio/speaking-author-sharp.jpg";

import portfolioCatering from "@/assets/portfolio/realtor-missy-sharp.jpg";
import portfolioDogGrooming from "@/assets/portfolio/maison-noir.png";
import portfolioFinancialAdvisor from "@/assets/portfolio/hardscapes-pavers-sharp.jpg";
import portfolioPhysicalTherapist from "@/assets/portfolio/physical-therapist.png";
import portfolioMedSpa from "@/assets/portfolio/realtor.png";
import portfolioCoffeeShop from "@/assets/portfolio/shakinah-realtor.png";
import portfolioLandscaping from "@/assets/portfolio/jameson-sons.png";
import portfolioAccounting from "@/assets/portfolio/accounting.png";
import portfolioKeynotes from "@/assets/portfolio/keynotes.png";
import portfolioHairSalon from "@/assets/portfolio/chicago-backyards-sharp.jpg";
import portfolioLsAdvisory from "@/assets/portfolio/ls-advisory.png";
import portfolioRestore from "@/assets/portfolio/restore-crisp.jpg";
import portfolioCornerCuppa from "@/assets/portfolio/corner-cuppa.png";
import portfolioSpaceGrace from "@/assets/portfolio/space-and-grace-crisp.jpg";

import avatarElaine from "@/assets/testimonials/avatar-elaine-B3rNOmMI.png";
import avatarTarrn from "@/assets/testimonials/avatar-tarrn-DBz18DZ_.png";
import avatarLissa from "@/assets/testimonials/avatar-lissa-B28uOaHv.png";
import avatarAhriana from "@/assets/testimonials/avatar-ahriana-BmR4_NC6.png";
import avatarCarrie from "@/assets/testimonials/avatar-carrie-DtFoRfBZ.png";
import trusted1 from "@/assets/trusted/coach-1.jpg";
import trusted2 from "@/assets/trusted/coach-2.jpg";
import trusted3 from "@/assets/trusted/coach-3.jpg";
import trusted4 from "@/assets/trusted/coach-4.jpg";
import trusted5 from "@/assets/trusted/coach-5.jpg";
import trusted6 from "@/assets/trusted/coach-6.jpg";
import trusted7 from "@/assets/trusted/coach-7.jpg";
import trusted8 from "@/assets/trusted/coach-8.jpg";
import trusted9 from "@/assets/trusted/coach-9.jpg";
import trusted10 from "@/assets/trusted/coach-10.jpg";
import trusted11 from "@/assets/trusted/coach-11.jpg";
import trusted12 from "@/assets/trusted/coach-12.jpg";
import trusted13 from "@/assets/trusted/coach-13.webp";
import trusted14 from "@/assets/trusted/coach-14.webp";
import trusted15 from "@/assets/trusted/coach-15.png";
import recentLaunchesBg from "@/assets/recent-launches-bg.jpg.asset.json";

const SITE = "https://threedaylaunch.com";
const PATH = "/chicagonewwebsitespecial";
const TITLE = "Chicago Web Designer for Small Business Owners | Three Day Launch";
const DESCRIPTION =
  "Chicago-based web designer building custom websites for small business owners. Launched in just 3 days, never a template. Custom multi-page websites.";
const OG_IMAGE = `${SITE}${sierraStairs.url}`;

function MobileStickyCTA() {
  return (
    <div className="sm:hidden fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 backdrop-blur px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
      <BookButton size="lg" className="mobile-sticky-cta w-full bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold h-14 rounded-full text-base">Book A Free Call</BookButton>
    </div>
  );
}

function BookButton({ children, ...props }: ComponentProps<typeof Button>) {
  return (
    <Button onClick={openLeadFormDialog} {...props}>
      {children}
    </Button>
  );
}

const trustedImages = [
  trusted7, trusted2, trusted11, trusted5, trusted14, trusted9, trusted1, trusted13,
  trusted4, trusted15, trusted8, trusted3, trusted12, trusted6, trusted10,
];

const businessTypes = [
  { icon: Briefcase, name: "Professional Services" },
  { icon: Wrench, name: "Home Services & Trades" },
  { icon: Target, name: "Coaching & Consulting" },
  { icon: Building2, name: "Law Firms" },
  { icon: Dumbbell, name: "Gyms & Studios" },
  { icon: Stethoscope, name: "Healthcare" },
];

const faqs = [
  {
    q: "Do you only work with businesses in Chicago?",
    a: "We're based in Chicagoland, IL but build websites for business owners across the globe.",
  },
  {
    q: "What kinds of businesses do you design websites for?",
    a: "Salons, online business owners, coaches, restaurants, gyms, plumbers, HVAC, clinics, photographers, contractors, consultants, real estate agents, lawn care, law and finance firms, and just about any service-based local business. We do not build e-commerce stores.",
  },
  {
    q: "Do you really launch in 3 days?",
    a: "Yes. Day one is your kickoff Zoom call. Day two is design day with feedback rounds. Day three we connect your domain and set up hosting so you go live.",
  },
  {
    q: "How much does a website cost?",
    a: "We're currently running a limited-time special for Chicago-based business owners to get your custom website for just $99 (regularly starting at $4,000).\n\nTo take advantage of the special, book a free call and let us know you'd like to launch with the limited-time $99 special.",
  },
  {
    q: "Will my website show up on Google?",
    a: "Yes. Every Three Day Launch website is built with on-page SEO baked in. Proper title tags, meta descriptions, semantic HTML, fast load times and mobile responsive.",
  },
  {
    q: "What about ongoing updates and hosting?",
    a: "We handle hosting setup at launch and offer website care plans to take care of your secure hosting, maintenance, edits, and ongoing support.",
  },
];

export const Route = createFileRoute("/chicagonewwebsitespecial")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [
      { rel: "canonical", href: `${SITE}${PATH}` },
      { rel: "preload", as: "image", href: heroPeople, fetchPriority: "high" } as unknown as Record<string, string>,
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "Three Day Launch — Chicago Web Designer for Small Business",
          image: OG_IMAGE,
          url: `${SITE}${PATH}`,
          telephone: "",
          description: DESCRIPTION,
          address: {
            "@type": "PostalAddress",
            addressLocality: "Chicago",
            addressRegion: "IL",
            addressCountry: "US",
          },
          areaServed: [
            { "@type": "City", name: "Chicago" },
            { "@type": "State", name: "Illinois" },
            "United States",
          ],
          founder: {
            "@type": "Person",
            name: "Sierra Janisse",
            jobTitle: "Founder & Web Designer",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Small business website design",
          provider: { "@type": "Organization", name: "Three Day Launch", url: SITE },
          areaServed: { "@type": "City", name: "Chicago" },
          name: "Custom small business website, launched in 3 days",
          description:
            "Custom website design for Chicago small business owners. Multi-page sites, mobile responsive, SEO-ready, launched in 3 days.",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: ChicagoPage,
});

function ChicagoPage() {
  return (
    <div className="mobile-single-cta min-h-screen bg-background text-foreground max-sm:pb-24 overflow-x-clip">
      <Header />
      <Hero />
      <div className="relative overflow-clip bg-background">
        <CursorGlow opacity={0.22} fadeBottom />
        <TrustedStrip />
        <Portfolio>
          <RecentWebsiteLaunches useLeadForm embedded />
        </Portfolio>
        <PowerTestimonials />
        <WhatsIncluded />
        <Process />
        <ValueSection />
      </div>
      <HowAffordable />
      <MeetSierra />
      <FAQ />
      <Testimonials />
      
      <FinalCTA />
      <ContactSection />
      <LogoStrip />
      <Footer />
      <FloatingVideoWidget src={heroIntroVideo.url} />
      <LeadFormDialog />
      <SpinToWinPopup source="chicagonewwebsitespecial-spin-popup" />
      <MobileStickyCTA />
    </div>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const navLinks = [
    { href: "#reviews", label: "Reviews" },
    { href: "#features", label: "Features" },
    { href: "#how-it-works", label: "How It Works" },
    { href: "#portfolio", label: "Portfolio" },
    { href: "#faq", label: "FAQ" },
    { href: "#contact", label: "Contact" },
  ];
  return (
    <header className="absolute top-0 left-0 right-0 z-50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-5 flex flex-wrap items-center justify-between gap-3 sm:gap-6">
        <div className="flex items-center gap-2 text-white shrink-0 min-w-0">
          <div className="h-9 w-9 rounded-lg bg-primary-gradient flex items-center justify-center shadow-glow shrink-0">
            <Rocket className="h-5 w-5 text-white" />
          </div>
          <span className="font-bold tracking-tight text-base sm:text-lg">
            Three Day Launch<sup className="text-xs ml-0.5">®</sup>
          </span>
        </div>
        <nav className="hidden lg:flex items-center gap-6 text-sm font-medium text-white/85">
          {navLinks.map((l) => (
            <a key={l.href} href={l.href} className="hover:text-white transition-colors">{l.label}</a>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2">
          <BookButton className="hidden sm:inline-flex bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold rounded-full px-3 sm:px-5 text-xs sm:text-sm shrink-0">Book A Free Call</BookButton>
          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-lg bg-white/10 backdrop-blur border border-white/15 text-white hover:bg-white/20 transition-colors"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden mx-4 sm:mx-6 rounded-2xl bg-black/80 backdrop-blur-md border border-white/15 overflow-hidden">
          <nav className="flex flex-col py-2">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="px-5 py-3 text-sm font-medium text-white/90 hover:bg-white/10 transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden pt-28 pb-20 md:pt-36 md:pb-28">
      <img
        src={heroPeople}
        alt=""
        aria-hidden="true"
        width={1600}
        height={1066}
        fetchPriority="high"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover object-left md:object-center"
      />
      <div className="absolute inset-0 bg-black/45" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/40 to-black/60" />

      <div className="relative mx-auto max-w-7xl px-6 grid md:grid-cols-2 gap-12 items-start">
        <div className="text-white min-w-0 md:pt-8">
          <div className="text-xs sm:text-sm font-semibold tracking-[0.18em] uppercase text-primary mb-3">
            Chicago Custom Website Design
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl leading-[1.15] md:leading-[1.1] font-bold [text-wrap:balance]">
            Go live in just <span className="text-highlight">3 days</span> <span className="text-white">with a professional website that helps</span> clients find you.
          </h1>
          <div className="mt-8">
            <ul className="mt-6 space-y-3">
            {[
              "Custom designed, never a template",
              "Multi-page, mobile responsive websites",
              "Built-in SEO so clients and customers find you",
              "Launched start to finish in 72 hours.",
            ].map((t) => (
                <li key={t} className="flex items-center gap-3 text-white/90">
                  <span className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center">
                    <Check className="h-3.5 w-3.5 text-primary" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <BookButton size="lg" className="hidden sm:inline-flex bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center">Book A Free Call <ArrowRight className="ml-2 h-4 w-4" /></BookButton>
          </div>
        </div>
        <LeadCard />
      </div>
    </section>
  );
}

function LeadCard() {
  const [finePrintOpen, setFinePrintOpen] = useState(false);
  return (
    <div className="relative w-full min-w-0 bg-card text-card-foreground rounded-3xl p-6 sm:p-8 shadow-card-soft border border-border/40">
      <div className="absolute -top-4 left-8 bg-primary text-primary-foreground inline-flex items-center text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
        LIMITED TIME SPECIAL · SEPTEMBER, 2026
      </div>
      <h2 className="text-2xl font-bold mt-6 md:mt-2 text-black">
        Back to School. Back to Business.
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        Get your custom website for <span className="line-through opacity-70">$4,000</span>{" "}
        <span className="font-bold">just</span> <span className="font-bold text-primary">$99</span>{" "}
        with our special Back to Business rate.
      </p>
      <p className="mt-1.5 text-sm text-muted-foreground">
        Why so cheap?{" "}
        <button
          type="button"
          onClick={() => setFinePrintOpen(true)}
          className="underline underline-offset-2 hover:text-foreground transition-colors"
        >
          Click HERE for the fine print
        </button>.
      </p>
      <HeroLeadForm />
      <Dialog open={finePrintOpen} onOpenChange={setFinePrintOpen}>
        <DialogContent className="max-w-md max-h-[85dvh] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl sm:text-2xl font-bold leading-snug text-left">
              Why a $99 website? It's simple: We want to become your website people.
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="sr-only">
            The fine print behind the $99 special website offer.
          </DialogDescription>
          <div className="space-y-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
            <p>
              This limited-time special gives us the chance to wow you with a custom website and an experience you LOVE.
            </p>
            <p>
              That’s the not-so-secret sales agenda: As your business grows, we hope you’ll keep turning to us for updates, new pages, and SEO content.
            </p>
            <p>
              We’re thinking beyond launch day. We want to earn a place in your corner for years to come.
            </p>
            <p>
              But first, let us wow you with a website you love, at a crazy steal of a deal.
            </p>
            <p className="font-semibold text-foreground">
              <button
                type="button"
                onClick={() => {
                  setFinePrintOpen(false);
                  openLeadFormDialog();
                }}
                className="underline underline-offset-2 hover:opacity-80 transition-opacity"
              >
                Book a free call
              </button>.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LogoStrip() {
  const items = [
    { quote: "OMG I LOVEEEEE my new site!!!", name: "Rachel" },
    { quote: "This site looks boss.", name: "Dave" },
    { quote: "Just booked my first big client from the website!", name: "Beatrice" },
    { quote: "Customers are impressed the moment they land.", name: "Jenni" },
    { quote: "This site surpasses my wildest dreams.", name: "Beth" },
  ];
  return (
    <div data-drag-marquee className="bg-secondary text-secondary-foreground py-6 overflow-hidden border-y border-white/5">
      <div className="flex gap-16 marquee whitespace-nowrap text-sm font-semibold tracking-wide text-white/70 select-none [touch-action:pan-y]">
        {[...items, ...items, ...items].map((it, i) => (
          <span key={i} className="flex items-center gap-16">
            <span className="flex flex-col items-start leading-tight">
              <span className="italic">"{it.quote}"</span>
              <span className="text-xs text-white/70 normal-case mt-0.5">{it.name}</span>
            </span>
            <span className="h-1.5 w-1.5 rounded-full bg-primary/60" />
          </span>
        ))}
      </div>
    </div>
  );
}

function TrustedStrip() {
  return (
    <section className="relative py-12 md:py-16 overflow-hidden">
      <div className="text-center px-6">
        <h2 className="text-xl md:text-2xl font-bold text-foreground">
          <span className="text-primary">7 Years</span>&nbsp;Serving Business Owners
        </h2>
      </div>
      <div className="mt-8 relative overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
        <div className="flex gap-6 w-max animate-[marquee_40s_linear_infinite]">
          {[...trustedImages, ...trustedImages].map((src, idx) => (
            <div key={idx} className="h-28 w-24 md:h-32 md:w-28 shrink-0 rounded-2xl overflow-hidden bg-muted border border-border" aria-hidden="true">
              <img src={src} alt="" className="h-full w-full object-cover object-top" loading="lazy" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ValueSection() {
  return null; // section hidden for now — delete this line to restore

  return (
    <section className="relative overflow-hidden py-[45px]">
      <div className="mx-auto max-w-7xl px-6">
        <div className="relative rounded-3xl bg-hero-gradient text-white p-10 md:p-16 overflow-hidden shadow-card-soft">
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-primary/30 blur-3xl" />
          <div className="absolute -left-20 -bottom-20 h-72 w-72 rounded-full bg-highlight/20 blur-3xl" />
          <div className="relative grid md:grid-cols-[1.4fr_1fr] gap-10 items-center">
            <div>
              <h2 className="text-3xl md:text-5xl font-bold leading-tight">
                <span className="text-primary">Attract</span> leads & <span className="text-primary">win</span> new customers
              </h2>
              <p className="mt-5 text-white/75 text-lg max-w-xl">
                Stop losing Chi-town clients to that "meh" competitor who just happens to have a site that doesn’t look like a 2008 Craigslist ad. <br /><br />Your business is incredible. Your digital curbside appeal? It’s currently telling people you’re closed for the season.
              </p>
              <div className="mt-8 grid sm:grid-cols-3 gap-4">
                {[
                  { icon: Zap, label: "Live in 3 days" },
                  { icon: Palette, label: "100% custom design" },
                  { icon: ShieldCheck, label: "Strategic, multi-page sites" },
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                    <Icon className="h-5 w-5 text-primary" />
                    <span className="text-sm font-semibold">{label}</span>
                  </div>
                ))}
              </div>
              <BookButton size="lg" className="mt-8 bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center">Book A Free Call <ArrowRight className="ml-2 h-4 w-4" /></BookButton>
            </div>
            <div className="relative">
              <div className="bg-white text-foreground rounded-3xl p-8 pb-12 shadow-2xl rotate-2">
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">THE SIGNATURE LAUNCH</div>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="text-5xl font-bold text-primary">3 Day Launch®</span>
                </div>
                <div className="mt-4 h-px bg-border" />
                <ul className="mt-4 space-y-2 text-sm">
                  {["Mobile Responsive Website", "SSL Certificate (HTTPS)", "SEO-Supercharged", "Built & Launched In 3 Days", "Ongoing Maintenance & Support"].map(i => (
                    <li key={i} className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> {i}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

type QuotePart = { text: string; highlight?: boolean };
const powerTestimonials: { name: string; parts: QuotePart[] }[] = [
  {
    name: "Ted C.",
    parts: [
      { text: "Just wanted to share that the new site, offers, and opt-in flow are all a resounding success! " },
      { text: "My customer base went from eight to 40 in a week.", highlight: true },
      { text: " So excited for this new stage! Thank you for being a part of this success." },
    ],
  },
  {
    name: "Beth H.",
    parts: [
      { text: "Working with Sierra doesn't just give you a 'pretty face,' it elevates your business's authority with your target customer. " },
      { text: "We recouped our investment in the first month after launch, thanks to increased sales through our website.", highlight: true },
    ],
  },
  {
    name: "Jasmine M.",
    parts: [
      { text: "My favorite part is how everything finally works together as a cohesive, modern home. " },
      { text: "I made my first sale within the first 30 days of launch.", highlight: true },
    ],
  },
  {
    name: "Dave S.",
    parts: [
      { text: "My website has " },
      { text: "graduated from 'start up vibes' into 'established business.'", highlight: true },
      { text: " I'm astonished by how much we accomplished in 3 days." },
    ],
  },
];

function PowerTestimonials() {
  return (
    <section id="reviews" className="relative py-12 md:py-24 overflow-hidden scroll-mt-24">
      <div className="relative mx-auto max-w-7xl px-6">
        <div className="text-center mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold">
            Real Client&nbsp;<span className="text-primary">Wins</span>
          </h2>
        </div>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {powerTestimonials.map((t) => (
            <figure key={t.name} className="bg-card border border-border rounded-2xl p-7 shadow-card-soft flex flex-col">
              <blockquote className="text-foreground/85 leading-relaxed flex-1">
                <span className="text-primary font-serif text-3xl leading-none mr-1">“</span>
                {t.parts.map((p, i) => (
                  p.highlight
                    ? <span key={i} className="bg-primary/20 text-foreground rounded px-1">{p.text}</span>
                    : <span key={i}>{p.text}</span>
                ))}
                <span className="text-primary font-serif text-3xl leading-none ml-1">”</span>
              </blockquote>
              <figcaption className="mt-5 font-bold text-foreground">{t.name}</figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhatsIncluded() {
  const websiteItems = [
    "4-page custom website design (never a template!)",
    "Mobile responsive so your pages look amazing and load fast on every device",
    "Copywriting (website words) done for you",
    "SEO (Search Engine Optimization) so your website shows up in the right Google and AI searches",
    "Lead capture routed straight to your inbox or CRM to capture engaged visitors",
    "Accessibility built in, so every visitor can engage and your site meets modern compliance standards",
  ];
  const supportItems = [
    "Secure hosting & maintenance handled for you",
    "Ongoing updates and edits your site  can evolve as you grow",
    "Only a message away should you need updates or help",
  ];
  const blocks = [
    { label: "YOUR CUSTOM WEBSITE", title: "Your custom website.", items: websiteItems },
    { label: "YOUR ONGOING SUPPORT", title: "Ongoing support.", items: supportItems },
  ];
  const optionalServices = (
    <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur p-7">
      <div className="text-xs font-bold uppercase tracking-wider text-primary">OPTIONAL ADDITIONAL SERVICES</div>
      <div className="mt-4 grid sm:grid-cols-3 gap-4">
        <div className="flex items-start gap-3">
          <Layers className="h-5 w-5 mt-0.5 shrink-0 text-primary" />
          <p className="text-sm text-white/75 leading-relaxed"><span className="font-semibold text-white">More pages.</span> Need more than 4 pages? Add service pages, galleries, a blog, and more.</p>
        </div>
        <div className="flex items-start gap-3">
          <Bot className="h-5 w-5 mt-0.5 shrink-0 text-primary" />
          <p className="text-sm text-white/75 leading-relaxed"><span className="font-semibold text-white">AI chatbots &amp; assistants.</span> Engage visitors 24/7 with smart chatbots that answer questions and qualify leads while you're off the clock.</p>
        </div>
        <div className="flex items-start gap-3">
          <PenTool className="h-5 w-5 mt-0.5 shrink-0 text-primary" />
          <p className="text-sm text-white/75 leading-relaxed"><span className="font-semibold text-white">SEO content done for you.</span> Blog posts written and published for you to boost your visibility in search.</p>
        </div>
      </div>
    </div>
  );
  return (
    <section id="features" className="relative py-16 md:py-24 overflow-clip bg-secondary text-secondary-foreground scroll-mt-24">
      <div aria-hidden="true" className="absolute inset-0 opacity-60" style={{
        backgroundImage: "radial-gradient(circle at 15% 20%, color-mix(in oklab, var(--primary) 35%, transparent) 0%, transparent 45%), radial-gradient(circle at 85% 80%, color-mix(in oklab, var(--primary) 25%, transparent) 0%, transparent 50%)",
      }} />
      <div aria-hidden="true" className="absolute -top-32 -left-32 h-80 w-80 rounded-full bg-primary/30 blur-3xl" />
      <div aria-hidden="true" className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-primary/20 blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-6">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="mt-4 text-3xl md:text-5xl font-bold tracking-tight text-white">
            Everything your website needs to <span className="text-primary">win clients.</span>
          </h2>
        </div>
        <div className="mt-14 grid gap-6 lg:grid-cols-[minmax(0,1fr)_420px] lg:gap-8">
          <div className="space-y-6 lg:self-start">
            {blocks.map((b) => (
              <div key={b.label} className="bg-white/5 border border-white/10 rounded-3xl p-7 backdrop-blur">
                <h3 className="text-xl font-bold text-white">{b.title}</h3>
                <ul className="mt-5 space-y-3">
                  {b.items.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-white/75 leading-relaxed">
                      <Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            <div className="hidden lg:block">{optionalServices}</div>
          </div>
          <div className="lg:self-stretch">
          <div className="rounded-3xl border border-primary/40 bg-card text-card-foreground p-8 text-center shadow-glow lg:sticky lg:top-24">
             <div className="text-xs font-bold uppercase tracking-wider text-primary">LIMITED TIME SPECIAL · SEPTEMBER, 2026</div>
            <div className="mt-3 flex flex-col items-center gap-1 md:flex-row md:items-baseline md:justify-center md:gap-3">
              <span className="text-xl font-semibold text-muted-foreground line-through decoration-2">Regularly $4,000</span>
              <span className="text-5xl font-bold tracking-tight">$99</span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">Your complete custom website design, ready to convert clients and customers.</p>
            <div className="my-5 h-px bg-border" />
            
            <div className="mt-6 flex justify-center">
              <BookButton size="lg" className="mobile-cta-keep bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center">Book A Free Call <ArrowRight className="ml-2 h-4 w-4" /></BookButton>
            </div>
          </div>
          </div>
          <div className="lg:hidden">{optionalServices}</div>
        </div>
        <div className="mt-12">
          <p className="text-center text-lg sm:text-xl font-semibold text-white leading-relaxed max-w-2xl mx-auto">
            Storefront or service-based, solo or small team. We build websites that turn visitors into inquiries for:
          </p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {businessTypes.map(({ icon: Icon, name }, i) => (
              <div key={`${name}-${i}`} className="group bg-white/5 rounded-3xl p-5 border border-white/10 backdrop-blur hover:border-primary/50 transition-all text-center">
                <div className="mx-auto h-11 w-11 rounded-xl bg-primary/15 flex items-center justify-center">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <div className="mt-3 font-semibold text-sm text-white">{name}</div>
              </div>
            ))}
          </div>
          <p className="text-center mt-6 text-sm text-white/90">
            …and dozens more. If clients look you up before buying, you're in the right place.
          </p>
        </div>
      </div>
    </section>
  );
}

function Portfolio({ children }: { children?: import("react").ReactNode }) {
  const projects = [
    { img: portfolioSpeakingAuthor, name: "Speaker + Author" },
    
    { img: portfolioCatering, name: "Luxury Realtor" },
    { img: portfolioDogGrooming, name: "Hair Salon" },
    { img: portfolioFinancialAdvisor, name: "Hardscapes & Pavers" },
    
    { img: portfolioKeynotes, name: "Keynote Speaker" },
    { img: portfolioCoffeeShop, name: "Realtor Studio" },
    { img: portfolioLandscaping, name: "Lawncare" },
    { img: portfolioAccounting, name: "Accounting" },
    { img: portfolioMedSpa, name: "Realtor" },
    { img: portfolioRestore, name: "Chicago Backyards" },
    { img: portfolioCornerCuppa, name: "Coffee Shop" },
    { img: portfolioSpaceGrace, name: "Faith & Wellness" },
  ];
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [activeGalleryIndex, setActiveGalleryIndex] = useState<number | null>(null);
  const galleryProjects = projects;
  return (
    <section id="portfolio" className="relative bg-secondary bg-cover bg-center bg-scroll pt-12 pb-4 md:py-24 text-secondary-foreground scroll-mt-24 md:bg-fixed" style={{ backgroundImage: `url(${recentLaunchesBg.url})` }}>
      <div className="absolute inset-0 bg-secondary/90" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-6">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="mt-4 text-4xl md:text-5xl text-white font-bold">
            Your old website is <span className="text-primary">already jealous.</span>
          </h2>
        </div>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {projects.map((p, i) => (
            <div key={p.name} className={`group ${i >= 4 && !showAllProjects ? "hidden md:block" : ""}`}>
              <div className="relative">
                <div className="relative bg-[#1a1a1a] rounded-t-2xl p-2 pt-6 border border-white/10">
                  <div className="flex gap-1.5 absolute top-2.5 left-4">
                    <span className="h-2 w-2 rounded-full bg-red-400/80" />
                    <span className="h-2 w-2 rounded-full bg-yellow-400/80" />
                    <span className="h-2 w-2 rounded-full bg-green-400/80" />
                  </div>
                  <MockupLightbox
                    img={p.img}
                    name={p.name}
                    onOpen={() => setActiveGalleryIndex(i)}
                  />
                </div>
                <div className="mx-auto h-3 max-w-full bg-gradient-to-b from-[#2a2a2a] to-[#0f0f0f] rounded-b-2xl" style={{ width: "108%" }} />
                <div className="mx-auto h-1 bg-black/60 rounded-b-full" style={{ width: "30%" }} />
              </div>
            </div>
          ))}
        </div>
        <PortfolioGallery projects={galleryProjects} activeIndex={activeGalleryIndex} onActiveIndexChange={setActiveGalleryIndex} />
        {!showAllProjects && projects.length > 4 && (
          <div className="mt-8 text-center md:hidden">
            <Button variant="link" onClick={() => setShowAllProjects(true)} className="text-secondary-foreground underline underline-offset-4 p-0 h-auto text-sm font-medium">
              Load More Designs
            </Button>
          </div>
        )}
        {children}
        <div className="mt-14 max-sm:mt-0 text-center">
          <BookButton size="lg" className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold h-auto min-h-14 py-3 px-6 sm:px-8 rounded-full shadow-glow text-sm sm:text-base max-w-full whitespace-normal text-center leading-snug">Book A Free Call</BookButton>
        </div>
      </div>
    </section>
  );
}

function HowAffordable() {
  return null; // section hidden for now — delete this line to restore
  return (
    <section className="py-12 md:py-24 bg-background">
      <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold leading-tight">
            Most Chicago agencies make you pick between speed and quality. <span className="text-foreground">We give you both.</span>
          </h2>
          <div className="mt-8 space-y-4">
            {[
              { t: "Lightning Fast Timelines", d: "Days instead of months, so you can start seeing ROI on your new website right away." },
              { t: "Custom Quality You Can Trust", d: "Every pixel designed for your business. Never a template." },
            ].map(({ t, d }) => (
              <div key={t} className="flex gap-4">
                <div className="h-10 w-10 rounded-xl bg-primary-gradient flex items-center justify-center flex-shrink-0 shadow-glow">
                  <Check className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="font-bold">{t}</div>
                  <div className="text-muted-foreground text-sm">{d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative">
          <div className="bg-secondary text-secondary-foreground rounded-3xl p-10 shadow-card-soft">
            <div className="text-xs font-bold uppercase tracking-wider text-white/80">The typical agency experience</div>
            <div className="mt-2 text-5xl font-bold line-through text-white/70">3–6 Months</div>
            <div className="mt-1 text-sm text-white/60">Endless revisions. Cookie-cutter template. You're just another ticket in the queue.</div>
            <div className="mt-8 h-px bg-white/10" />
             <div className="mt-6 text-xs font-bold uppercase tracking-wider text-primary">THE THREE DAY LAUNCH WAY</div>
             <div className="mt-2 text-4xl md:text-5xl font-bold text-primary whitespace-nowrap">Live in 3 Days</div>
             <div className="mt-1 text-sm text-white/70">A multi-page, custom website designed just for your business. Never a template.</div>
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-6 mt-12 text-center">
        <BookButton size="lg" className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center">Book A Free Call</BookButton>
      </div>
    </section>
  );
}

function Process() {
  const steps = [
    { n: "Day 1", t: "Meet over Zoom for your Strategy Call.", d: "One on one with your designer to get clear on your business and website goals." },
    { n: "Day 2", t: "We design your custom website, all in one day.", d: "No meetings this day. You just need to be available to provide feedback notes." },
    { n: "Day 3", t: "Domain connection & launch celebration.", d: "We hit publish, connect your domain (like yourbusiness.com), and you're officially online." },
  ];
  return (
    <section id="how-it-works" className="relative z-10 pt-12 pb-10 md:py-24 pt-[35px] scroll-mt-24" style={{ background: "linear-gradient(to bottom, color-mix(in oklab, var(--muted) 40%, transparent) 0%, color-mix(in oklab, var(--muted) 40%, transparent) 70%, transparent 100%)" }}>
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center max-w-3xl mx-auto">
          <div className="text-sm font-semibold uppercase tracking-widest text-primary mb-3">Our Trademarked Process</div>
          <h2 className="text-4xl md:text-5xl font-bold"><span className="text-primary">Three days,</span> and your custom website is ready to share.</h2>
          <p className="mt-4 text-muted-foreground text-lg">From "let's do it" to "we're live" in less time than most designers take to send a proposal.</p>
        </div>
        <div className="mt-8 md:mt-14 grid md:grid-cols-3 gap-6">
          {steps.map((s) => (
            <div key={s.n} className="bg-card rounded-3xl p-8 border border-border shadow-card-soft">
              <div className="text-6xl font-bold text-primary/50">{s.n}</div>
              <h3 className="mt-2 text-xl font-bold">{s.t}</h3>
              <p className="mt-3 text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 md:mt-12 text-center">
          <BookButton size="lg" className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center">Book A Free Call</BookButton>
        </div>
      </div>
    </section>
  );
}

function MeetSierra() {
  return (
    <section id="sierra" className="pb-16 md:pb-24 bg-background scroll-mt-24">
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid md:grid-cols-[1fr_1.2fr] gap-10 md:gap-14 items-start">
          <div className="relative mx-auto h-36 w-36 overflow-hidden rounded-full bg-muted shadow-card-soft sm:h-40 sm:w-40 md:mx-0 md:h-full md:w-full md:rounded-3xl">
            <img
              src={sierraStairs.url}
              alt="Sierra Janisse, Chicago-based web designer and founder of Three Day Launch"
              className="h-full w-full object-cover object-[center_20%] md:object-center"
              width={900}
              height={1125}
              loading="lazy"
              decoding="async"
            />
          </div>
          <div>
             <h2 className="text-4xl md:text-5xl font-bold leading-tight">Hi, I'm Sierra</h2>
            <div className="mt-2 text-primary font-semibold text-sm uppercase tracking-wider">
              AWARD WINNING WEB DESIGNER · FOUNDER & DESIGN LEAD
            </div>
            <div className="mt-6 space-y-5 text-muted-foreground text-lg leading-relaxed">
              <p>
                I’ve helped hundreds of business owners turn what makes their business special into a website they’re proud to share. I take the time to understand your business, explain your options in plain English (not designer lingo) and guide you through each decision. You bring your expertise. I bring the design direction and a clear plan to get your website live, so you can feel supported instead of overwhelmed.
              </p>
              <p>
                Three Day Launch exists because we believe every business owner deserves a website they're proud to share. An online home that works just as hard as you do to grow your business.
              </p>
            </div>
            <div className="mt-8">
              <BookButton size="lg" className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center">Book A Free Call <ArrowRight className="ml-2 h-4 w-4" /></BookButton>
            </div>
            <AuthorityBadges />
          </div>
        </div>
      </div>
    </section>
  );
}

const testimonials = [
  { quote: "Always pleasant, upbeat, and open to feedback, making the process smooth and enjoyable. Your creativity and attention to detail really shone through.", name: "Elaine E.", industry: "Therapist", avatar: avatarElaine },
  { quote: "You taught me more than I had previously learned from paying two other business advisors. And the experience of working together was fun!", name: "Tarrn M.", industry: "Orthodontist", avatar: avatarTarrn },
  { quote: "Absolutely amazing. Sierra really cares about her clients and helps them achieve their goals. You will not regret working with her.", name: "Lissa A.", industry: "Career Consulting", avatar: avatarLissa },
  { quote: "Now, this website feels like ME — like people can actually feel who I am and see what I'm up to! YOU TOTALLY ROCK!", name: "Ahriana P.", industry: "Author", avatar: avatarAhriana },
  { quote: "I love how you asked great questions and had creative ways of figuring out what we wanted. The end result really shows that.", name: "Carrie S.", industry: "Realtor", avatar: avatarCarrie },
];

function Testimonials() {
  const loop = [...testimonials, ...testimonials];
  return (
    <section className="pb-12 max-sm:pb-4 md:pb-24 bg-background overflow-hidden">
      <div className="mx-auto max-w-7xl px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold">Don't take our word for it,</h2>
          <p className="mt-2 text-4xl md:text-5xl font-bold text-primary">take theirs:</p>
        </div>
      </div>
      <div data-drag-marquee className="group relative w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_8%,black_92%,transparent)]">
        <div className="flex w-max gap-6 marquee animate-marquee group-hover:[animation-play-state:paused]">
          {loop.map((t, i) => (
            <figure key={`${t.name}-${i}`} className="w-[340px] md:w-[400px] shrink-0 rounded-2xl bg-card border border-border p-8 flex flex-col shadow-sm">
              <blockquote className="text-foreground/80 leading-relaxed flex-1">{t.quote}</blockquote>
              <figcaption className="mt-6 flex items-center gap-3">
                <img src={t.avatar} alt={t.name} className="h-12 w-12 rounded-full object-cover" loading="lazy" />
                <div>
                  <div className="font-semibold">{t.name}</div>
                  <div className="text-sm text-muted-foreground">{t.industry}</div>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-6 mt-16 max-sm:mt-0 text-center">
        <BookButton size="lg" className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center">Book A Free Call</BookButton>
      </div>
    </section>
  );
}

function FAQ() {
  return (
    <section id="faq" className="bg-background pb-[90px] max-sm:pb-10 scroll-mt-24">
      <div className="mx-auto max-w-3xl px-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary">
            FAQ
          </div>
          <h2 className="mt-4 text-4xl md:text-5xl font-bold">Questions? We've got answers.</h2>
        </div>
        <Accordion type="single" collapsible className="mt-10 space-y-3">
          {faqs.map((item, i) => (
            <AccordionItem key={i} value={`i-${i}`} className="bg-card rounded-2xl border border-border px-6 data-[state=open]:shadow-card-soft">
              <AccordionTrigger className="text-left font-semibold text-base hover:no-underline">{item.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground whitespace-pre-line">{item.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="mt-12 max-sm:mt-0 text-center">
          <BookButton size="lg" className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center">Book A Free Call <ArrowRight className="ml-2 h-4 w-4" /></BookButton>
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="py-20 bg-secondary text-secondary-foreground">
      <div className="mx-auto max-w-5xl px-6 text-center">
        <h2 className="text-4xl md:text-6xl font-bold">
          Your website. <span className="text-primary">Live in 3 days.</span>
        </h2>
        <p suppressHydrationWarning className="mt-5 text-white/70 text-lg max-w-xl mx-auto">
          Let's build a website that grows your business.
          <br /><br />
          Get started by scheduling a free, no-pressure call.
        </p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <BookButton size="lg" className="mobile-cta-keep bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center">Book A Free Call <ArrowRight className="ml-2 h-4 w-4" /></BookButton>
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", business: "", message: "" });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (form.name.trim().length < 1) return toast.error("Please enter your name.");
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return toast.error("Please enter a valid email.");
    if (form.message.trim().length < 10) return toast.error("Message must be at least 10 characters.");

    setSubmitting(true);
    try {
      const res = await fetch("/api/public/contact-notify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          business: form.business.trim() || null,
          message: form.message.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Failed to send");
      toast.success("Success! Your message has been sent.");
      setForm({ name: "", email: "", business: "", message: "" });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or email hello@threedaylaunch.com.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative overflow-hidden border-y border-border bg-gradient-to-br from-primary/10 via-background to-background scroll-mt-24">
      <div aria-hidden className="pointer-events-none absolute -top-24 -right-24 h-80 w-80 rounded-full bg-primary/20 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -bottom-32 -left-20 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
      <div className="relative mx-auto max-w-3xl px-6 py-20 md:py-24">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary">
            <Mail className="h-3.5 w-3.5" /> Get in touch
          </div>
          <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight">Let's talk about your website</h2>
          <p className="mt-4 text-muted-foreground text-lg max-w-2xl mx-auto">
            We'll get back to you within{" "}
            <span className="font-semibold text-foreground">24 business hours</span>.
          </p>
        </div>

        <div className="mt-10 bg-card/90 backdrop-blur rounded-3xl border border-border shadow-card-soft p-6 md:p-10">
          {submitted && (
            <div role="status" aria-live="polite" className="mb-6 flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/10 p-4">
              <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-foreground">Success! Your message has been sent.</p>
                <p className="text-sm text-muted-foreground mt-1">We'll be in touch within 24 business hours.</p>
              </div>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="contact-name">Name *</Label>
                <Input id="contact-name" name="name" value={form.name} onChange={handleChange} required maxLength={100} placeholder="Your name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact-email">Email *</Label>
                <Input id="contact-email" name="email" type="email" value={form.email} onChange={handleChange} required maxLength={255} placeholder="you@example.com" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-business">Business (optional)</Label>
              <Input id="contact-business" name="business" value={form.business} onChange={handleChange} maxLength={150} placeholder="Your business name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-message">Message *</Label>
              <Textarea id="contact-message" name="message" value={form.message} onChange={handleChange} required minLength={10} maxLength={2000} rows={6} placeholder="Tell us about your project..." />
            </div>
            <Button type="submit" size="lg" disabled={submitting} className="w-full md:w-auto">
              {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> Sending...</>) : "Send message"}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-secondary text-secondary-foreground border-t border-white/10 py-10">
      <div className="mx-auto max-w-7xl px-6 text-center text-sm text-white/80">© {new Date().getFullYear()} Three Day Launch.</div>
      <div className="mx-auto max-w-7xl px-6 mt-4 text-center text-sm text-white/80">
        Based in Chicago | Serving Clients Nationwide
      </div>
      <div className="mx-auto max-w-7xl px-6 mt-2 text-center text-xs text-white/60">
        Three Day Launch is an independent website design studio creating professional websites.
      </div>
      <div className="mx-auto max-w-7xl px-6 mt-3 text-center text-[11px] text-white/50">
        <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-white/80 hover:underline transition-colors">Privacy Policy</a>
        {" | "}
        <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="hover:text-white/80 hover:underline transition-colors">Terms &amp; Conditions</a>
      </div>
    </footer>
  );
}

function CursorGlow({ color = "var(--primary)", opacity = 0.18, fadeBottom = false }: { color?: string; opacity?: number; fadeBottom?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let raf = 0;
    let x = 0, y = 0;
    const onMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
      if (!raf) {
        raf = requestAnimationFrame(() => {
          el.style.setProperty("--gx", `${x}px`);
          el.style.setProperty("--gy", `${y}px`);
          raf = 0;
        });
      }
    };
    const parent = el.parentElement;
    parent?.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      parent?.removeEventListener("mousemove", onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  return (
    <div
      ref={ref}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 transition-opacity duration-300"
      style={{
        background: `radial-gradient(600px circle at var(--gx, 50%) var(--gy, 50%), color-mix(in oklab, ${color} ${opacity * 100}%, transparent), transparent 60%)`,
        ...(fadeBottom
          ? {
              WebkitMaskImage: "linear-gradient(to bottom, black 0%, black calc(100% - 320px), transparent 100%)",
              maskImage: "linear-gradient(to bottom, black 0%, black calc(100% - 320px), transparent 100%)",
            }
          : {}),
      }}
    />
  );
}
